import { Circle, GoogleMap, LoadScript } from "@react-google-maps/api"
import PropTypes from "prop-types"
import React from "react"

import { lockedGoogleMapOptions } from "../../../utils/googleMapsUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { normalizeWebsiteLocationKind } from "../utils/locationKindUtils"

const locationKind = (loc) => {
  const k = normalizeWebsiteLocationKind(loc?.kind)
  return k || null
}

// Convert km to meters for Google Maps Circle radius
const kmToMeters = (km) => Number(km) * 1000

// Default radius (in km) to show when address is hidden until booking confirmation
const HIDDEN_ADDRESS_RADIUS_KM = 2.5

const ServiceLocation = ({ locations, selectedLocation, practice, theme }) => {
  // Early return if practice is not provided
  if (!practice) {
    return null
  }

  const formatAddress = (location) => {
    if (!location) return ""
    const kind = locationKind(location)

    if (kind === "virtual") return "Virtual Session"
    if (kind === "home") return "At Your Home"

    // For office/physical locations
    if (kind === "office" || kind === "physical") {
      const secondary = location.secondaryAddress ?? location.secondary_address
      if (location.address) {
        return secondary ? `${location.address}, ${secondary}` : location.address
      }
      if (location.city && (location.stateAbbreviation ?? location.state_abbreviation)) {
        return `${location.city}, ${location.stateAbbreviation ?? location.state_abbreviation}`
      }
      if (location.city && location.state) {
        return `${location.city}, ${location.state}`
      }
      return "In Office"
    }

    const stateAbbr = location.stateAbbreviation ?? location.state_abbreviation
    if (location.city && stateAbbr) {
      return `${location.city}, ${stateAbbr}`
    }
    return location.address || ""
  }

  // For in-person (office/physical): City, ST in larger font; street address below
  const getLocationDisplayParts = (location) => {
    const kind = locationKind(location)
    if (!location || (kind !== "office" && kind !== "physical")) return null
    const stateAbbr = location.stateAbbreviation ?? location.state_abbreviation
    const cityState =
      location.city && (stateAbbr || location.state) ? `${location.city}, ${stateAbbr || location.state}` : null
    const secondary = location.secondaryAddress ?? location.secondary_address
    const streetAddress =
      location.address && location.address.trim() ? [location.address, secondary].filter(Boolean).join(", ") : null
    return { cityState, streetAddress }
  }

  const getDisplayLocation = () => {
    if (selectedLocation) {
      return selectedLocation
    } else if (locations && locations.length > 0) {
      // Default to first in-person location (office/physical) so Location section shows when available
      return locations.find((loc) => locationKind(loc) === "office" || locationKind(loc) === "physical") || locations[0]
    }
    return null
  }

  const displayLocation = getDisplayLocation()

  // Get practice location data for map
  // Returns { lat, lng, address, hideAddress } for office/physical locations
  // IMPORTANT: hideAddress always comes from displayLocation (the selected service location),
  // even if we need to get lat/lng from practice.locations as a fallback
  const getPracticeLocationData = () => {
    const displayKind = locationKind(displayLocation)

    // Only get location data for office/physical locations (not home/virtual)
    if (displayKind !== "office" && displayKind !== "physical") {
      return null
    }

    // The hide_address setting from the SELECTED location is authoritative
    const hideAddr = displayLocation?.hide_address || displayLocation?.hideAddress || false

    // Try selected location first if it has coordinates
    if (displayLocation.latitude && displayLocation.longitude) {
      return {
        lat: displayLocation.latitude,
        lng: displayLocation.longitude,
        address: hideAddr ? null : formatAddress(displayLocation),
        hideAddress: hideAddr
      }
    }

    // Fallback: resolve same location from practice.locations by id (may have lat/lng)
    // But keep using hideAddr from displayLocation (the service's location setting)
    if (practice?.locations?.length > 0 && displayLocation?.id != null) {
      const fullLocation = practice.locations.find((loc) => String(loc.id) === String(displayLocation.id))
      if (fullLocation && fullLocation.latitude && fullLocation.longitude) {
        return {
          lat: fullLocation.latitude,
          lng: fullLocation.longitude,
          address: hideAddr ? null : formatAddress(fullLocation),
          hideAddress: hideAddr
        }
      }
    }

    // Try any practice office/physical location with coordinates for lat/lng only
    // But still use hideAddr from the selected displayLocation
    if (practice?.locations?.length > 0) {
      const officeLocation = practice.locations.find(
        (loc) => (locationKind(loc) === "office" || locationKind(loc) === "physical") && loc.latitude && loc.longitude
      )
      if (officeLocation) {
        return {
          lat: officeLocation.latitude,
          lng: officeLocation.longitude,
          address: hideAddr ? null : formatAddress(officeLocation),
          hideAddress: hideAddr
        }
      }
    }

    // Finally try practice.location (personal location) as last resort for coordinates
    if (
      practice?.location &&
      typeof practice.location === "object" &&
      practice.location.latitude &&
      practice.location.longitude
    ) {
      return {
        lat: practice.location.latitude,
        lng: practice.location.longitude,
        address: hideAddr
          ? null
          : practice.location.city && practice.location.state
          ? `${practice.location.city}, ${practice.location.state}`
          : practice.location.address || null,
        hideAddress: hideAddr
      }
    }

    return null
  }

  const locationData = getPracticeLocationData()

  // Don't show location section for:
  // - Virtual locations - no physical location to show
  // - "At home" locations - service is at client's home, not practitioner's
  // - If no display location is available
  const displayKind = locationKind(displayLocation)
  if (!displayLocation || displayKind === "virtual" || displayKind === "home") {
    return null
  }

  // For office/physical, we need either an address to show OR coordinates for the hidden-address circle
  if (!locationData?.address && !(locationData?.lat && locationData?.lng)) {
    return null
  }

  const hideAddress = locationData?.hideAddress

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 overflow-hidden">
      <h3
        className="w-full text-[28px] font-bold leading-[40px]"
        style={{
          fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
          color: theme?.colors?.textDark || "#55433f"
        }}>
        Location
      </h3>

      <div className="flex w-full items-center justify-start gap-4">
        <div className="flex flex-1 flex-col items-start justify-center gap-6">
          {/* Map - office/physical with visible address: embedded place map with exact pin */}
          {!hideAddress && locationData?.address && (
            <div
              className="relative h-[230px] w-full overflow-hidden bg-gray-200"
              style={{
                borderRadius: theme?.borderRadius || "12px"
              }}>
              <iframe
                className="pointer-events-none size-full"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=${
                  process.env.GOOGLE_MAPS_API_KEY
                }&q=${encodeURIComponent(locationData.address)}`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}

          {/* Map - office/physical with hidden address: show area circle (address revealed after booking) */}
          {hideAddress && locationData?.lat != null && locationData?.lng != null && (
            <div
              className="relative h-[230px] w-full overflow-hidden bg-gray-200"
              style={{
                borderRadius: theme?.borderRadius || "12px"
              }}>
              <LoadScript googleMapsApiKey={process.env.GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={{ lat: locationData.lat, lng: locationData.lng }}
                  zoom={12}
                  options={lockedGoogleMapOptions}>
                  <Circle
                    center={{ lat: locationData.lat, lng: locationData.lng }}
                    radius={kmToMeters(HIDDEN_ADDRESS_RADIUS_KM)}
                    options={{
                      strokeColor: theme?.colors?.primary || "#bd654f",
                      strokeWeight: 3,
                      fillColor: theme?.colors?.primary || "#bd654f",
                      fillOpacity: 0.2
                    }}
                  />
                </GoogleMap>
              </LoadScript>
            </div>
          )}

          {/* Address */}
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start justify-start gap-1">
              {hideAddress ? (
                /* Hidden address - show city/state area only with note about exact address */
                <div className="flex flex-col items-start gap-1">
                  {(displayLocation.city || locationData?.lat) && (
                    <div
                      className="text-[24px] font-semibold leading-[32px]"
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        color: theme?.colors?.textDark || "#55433f"
                      }}>
                      {displayLocation.city &&
                      (displayLocation.stateAbbreviation || displayLocation.state_abbreviation || displayLocation.state)
                        ? `${displayLocation.city}, ${
                            displayLocation.stateAbbreviation ||
                            displayLocation.state_abbreviation ||
                            displayLocation.state
                          }`
                        : displayLocation.city || "In this area"}
                    </div>
                  )}
                  <div
                    className="text-[14px] font-normal leading-[20px]"
                    style={{
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      color: theme?.colors?.textGrey || "#666666"
                    }}>
                    Exact address provided after booking
                  </div>
                </div>
              ) : (
                /* Visible address - show full details */
                (() => {
                  const parts = getLocationDisplayParts(displayLocation)
                  if (parts && (parts.cityState || parts.streetAddress)) {
                    return (
                      <div className="flex flex-col items-start gap-1">
                        {parts.cityState && (
                          <div
                            className="text-[24px] font-semibold leading-[32px]"
                            style={{
                              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                              color: theme?.colors?.textDark || "#55433f"
                            }}>
                            {parts.cityState}
                          </div>
                        )}
                        {parts.streetAddress && (
                          <div
                            className="text-[16px] font-normal leading-[24px]"
                            style={{
                              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                              color: theme?.colors?.textGrey || "#666666"
                            }}>
                            {parts.streetAddress}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return (
                    <div
                      className="text-[18px] font-normal leading-[32px]"
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        color: theme?.colors?.textDark || "#55433f"
                      }}>
                      {formatAddress(displayLocation)}
                    </div>
                  )
                })()
              )}
              {/* Show additional location details if available (only when address is visible) */}
              {!hideAddress && (
                <div className="flex flex-col items-start justify-start gap-1">
                  {displayLocation.name && (
                    <div
                      className="text-[16px] font-medium leading-[24px]"
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        color: theme?.colors?.textDark || "#55433f"
                      }}>
                      {displayLocation.name}
                    </div>
                  )}
                  {displayLocation.phone && (
                    <div
                      className="text-[14px] font-normal leading-[20px]"
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        color: theme?.colors?.textGrey || "#666666"
                      }}>
                      {displayLocation.phone}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* View map button - only show when address is visible */}
            {!hideAddress && locationData?.address && (
              <button
                onClick={() => {
                  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    locationData.address
                  )}`
                  window.open(mapUrl, "_blank")
                }}
                className="font-semibold underline decoration-solid transition-colors hover:no-underline"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: theme?.colors?.primary || "#bd654f",
                  borderRadius: theme?.buttonShape?.borderRadius || "0.375rem"
                }}>
                View map
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

ServiceLocation.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      city: PropTypes.string,
      stateAbbreviation: PropTypes.string,
      state: PropTypes.string,
      address: PropTypes.string,
      secondaryAddress: PropTypes.string,
      name: PropTypes.string,
      phone: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]).isRequired,
      hide_address: PropTypes.bool,
      travel_distance: PropTypes.string
    })
  ),
  selectedLocation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    city: PropTypes.string,
    stateAbbreviation: PropTypes.string,
    state: PropTypes.string,
    address: PropTypes.string,
    secondaryAddress: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]).isRequired,
    hide_address: PropTypes.bool,
    travel_distance: PropTypes.string
  }),
  practice: PropTypes.shape({
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
        address: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        hide_address: PropTypes.bool
      })
    ]),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]),
        city: PropTypes.string,
        stateAbbreviation: PropTypes.string,
        address: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        hide_address: PropTypes.bool
      })
    )
  }),
  theme: PropTypes.object
}

export default ServiceLocation
